import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCalendarPlus,
  FaTrophy,
  FaTimes,
  FaQuoteLeft,
  FaCheckCircle,
  FaRocket,
  FaArrowRight,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import "./Announcement.css";

/**
 * Default timezone offset for India Standard Time (IST).
 * When env timestamps do not specify a timezone (e.g. 20260829T22:30:00:000),
 * this ensures all users globally synchronize to the exact same instant in IST.
 */
const DEFAULT_TIMEZONE_OFFSET = "+05:30";

/**
 * Parses multiple date formats safely (ISO, YYYY-MM-DD, compact YYYYMMDDTHH:MM:SS:000, etc.)
 * Anchors timestamps without an explicit timezone to IST (+05:30) so release times
 * are universally synchronized worldwide regardless of the student's device timezone.
 */
export const parseFlexibleDate = (dateStr, defaultOffset = DEFAULT_TIMEZONE_OFFSET) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const raw = dateStr.trim();
  if (!raw) return null;

  // Check if string already contains a timezone indicator (Z or +HH:MM / -HH:MM / +HHMM)
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);

  // 1. Compact format: 20260829T22:30:00:000 or 20260829T22:30:00 or 20260829
  const compactMatch = raw.match(
    /^(\d{4})(\d{2})(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?::(\d{3}))?)?(.*)$/,
  );
  if (compactMatch) {
    const [, y, m, day, hr = "00", min = "00", sec = "00", ms = "000", tz = ""] =
      compactMatch;
    const finalTz = tz ? (tz.startsWith(":") ? tz.slice(1) : tz) : defaultOffset;
    const isoString = `${y}-${m}-${day}T${hr}:${min}:${sec}.${ms}${finalTz}`;
    const parsed = new Date(isoString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // 2. Standard ISO with Time: e.g. 2026-08-29T22:30:00
  if (raw.includes("T")) {
    const isoString = hasTz ? raw : `${raw}${defaultOffset}`;
    const parsed = new Date(isoString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // 3. Date-only: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = new Date(`${raw}T00:00:00.000${defaultOffset}`);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // 4. Standard Date fallback
  const fallback = new Date(raw);
  if (!isNaN(fallback.getTime())) {
    return fallback;
  }

  return null;
};

/**
 * Validates if the current date/time is within the configured qualification announcement range.
 */
export const isQualificationDateActive = () => {
  const startRaw = (
    process.env.REACT_APP_QUALIFICATION_STATUS_MESSAGE_START_DATE ||
    process.env.REACT_QUALIFICATION_STATUS_MESSAGE_START_DATE ||
    ""
  ).trim();

  const endRaw = (
    process.env.REACT_APP_QUALIFICATION_STATUS_MESSAGE_END_DATE ||
    process.env.REACT_QUALIFICATION_STATUS_MESSAGE_END_DATE ||
    ""
  ).trim();

  // If neither date is specified, default to inactive
  if (!startRaw && !endRaw) {
    return false;
  }

  const now = new Date();

  // Validate start date (inclusive)
  if (startRaw) {
    const startDate = parseFlexibleDate(startRaw);
    if (startDate && now < startDate) {
      return false;
    }
  }

  // Validate end date (inclusive)
  if (endRaw) {
    let endDate = parseFlexibleDate(endRaw);
    if (endDate) {
      if (!endRaw.includes("T") && !endRaw.includes(":")) {
        endDate = new Date(
          endDate.getTime() + (23 * 3600 + 59 * 60 + 59) * 1000 + 999
        );
      }
      if (now > endDate) {
        return false;
      }
    }
  }

  return true;
};

export default function Qualified({
  forceOpen = false,
  onCloseCallback = null,
  onViewPoolCallback = null,
  poolClassId = null,
  userName = "",
}) {
  const navigate = useNavigate();
  const isDateEligible = useMemo(() => isQualificationDateActive(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);

  // Target Phase 2 Date & Time: Sunday, 6th September 2026 at 10:00 AM IST (UTC+05:30)
  const targetDate = useMemo(() => {
    return new Date("2026-09-06T10:00:00+05:30");
  }, []);

  // Countdown timer state - days : hh : mm
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    isPast: false,
  });

  // Calculate remaining time
  const calculateTimeLeft = useCallback(() => {
    const difference = targetDate.getTime() - new Date().getTime();
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, isPast: true };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    return {
      days,
      hours,
      minutes,
      isPast: false,
    };
  }, [targetDate]);

  // Update countdown live
  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeLeft]);

  // Handle initial auto-open
  useEffect(() => {
    if (!isDateEligible && !forceOpen) return;

    if (forceOpen) {
      setIsOpen(true);
      return;
    }

    const dismissedAt = localStorage.getItem(
      "grademe_phase2_qualified_dismissed_at",
    );
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const hoursSinceDismissal =
        (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissal < 24) {
        // Dismissed within last 24h -> keep modal closed initially, show floating badge
        return;
      }
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 700);

    return () => clearTimeout(timer);
  }, [isDateEligible, forceOpen]);

  // Close modal handler
  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    if (dontShowToday) {
      localStorage.setItem(
        "grademe_phase2_qualified_dismissed_at",
        new Date().toISOString(),
      );
    }
    if (onCloseCallback) onCloseCallback();
  };

  // Quick re-open from floating trigger
  const handleOpen = () => {
    setIsOpen(true);
  };

  // Google Calendar Integration for Sunday, 6th September 2026 at 10:00 AM IST
  const handleAddGoogleCalendar = () => {
    const title = encodeURIComponent(
      "GradeMe AI - UNOSQ Phase 2 Exam",
    );
    const details = encodeURIComponent(
      "Congratulations on qualifying for Phase 2! The UNOSQ '26 Phase 2 exam is scheduled for Sunday, 6th September 2026 at 10:00 AM IST.\n\nPlease log in to GradeMe AI to access your assigned pool, test instructions, and practice tests.\n\nPortal: " +
        window.location.origin,
    );
    const location = encodeURIComponent("GradeMe AI Student Portal");
    const gCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=20260906T043000Z/20260906T073000Z&details=${details}&location=${location}`;
    window.open(gCalUrl, "_blank", "noopener,noreferrer");
  };

  const handleViewPool = () => {
    handleClose();
    if (onViewPoolCallback) {
      onViewPoolCallback();
    } else if (poolClassId) {
      navigate(`/student/view-class/${poolClassId}`);
    } else {
      navigate("/student/classes");
    }
  };

  const handleGoToPractice = () => {
    handleClose();
    navigate("/student/practice-tests");
  };

  // If outside active date range and not forced open, render nothing
  if (!isDateEligible && !forceOpen) {
    return null;
  }

  return (
    <>
      {/* Floating Interactive Trigger Pill (Shown when modal is closed) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            className="p1-floating-trigger-container"
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 30 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <motion.button
              className="p1-floating-pill p1-pill-qualified"
              onClick={handleOpen}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              aria-label="View Phase 2 Qualification Announcement"
            >
              <span className="p1-floating-pulse-ring q-pulse-ring"></span>
              <span className="p1-floating-icon p1-icon-gold">
                <FaTrophy />
              </span>
              <div className="p1-floating-text">
                <span className="p1-floating-title">
                  🎉 Qualified for Phase 2!
                </span>
                <span className="p1-floating-sub">
                  Phase 2 on Sunday, 6th Sept
                </span>
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Animated Modal Overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="p1-modal-backdrop-wrapper">
            <motion.div
              className="p1-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            />

            <div className="p1-modal-center-container">
              <motion.div
                className="p1-modal-card p1-card-qualified"
                initial={{ opacity: 0, scale: 0.88, y: 40 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: { type: "spring", stiffness: 320, damping: 28 },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.88,
                  y: 40,
                  transition: { duration: 0.22 },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Ambient Decorative Background Glows */}
                <div className="p1-glow-top-left p1-glow-gold"></div>
                <div className="p1-glow-bottom-right p1-glow-emerald"></div>
                <div className="p1-particles-overlay">
                  <span className="p1-particle p1-p1">✨</span>
                  <span className="p1-particle p1-p2">🏆</span>
                  <span className="p1-particle p1-p3">🎉</span>
                  <span className="p1-particle p1-p4">⭐</span>
                </div>

                {/* Modal Close Button */}
                <motion.button
                  className="p1-close-btn"
                  onClick={handleClose}
                  whileHover={{ scale: 1.15, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Close Announcement"
                >
                  <FaTimes />
                </motion.button>

                {/* Top Celebratory Header Badge */}
                <div className="q-badge-header">
                  <span className="q-tag-pill">
                    <HiSparkles /> Phase 1 Results Declared
                  </span>
                  <div className="q-trophy-badge">
                    <FaTrophy />
                  </div>
                </div>

                {/* Main Heading */}
                <div className="q-header-text">
                  <h2 className="q-title">
                    Congratulations{userName ? `, ${userName}` : ""}! 🎉
                  </h2>
                  <p className="q-subtitle">
                    You have successfully <strong>Qualified for Phase 2</strong>{" "}
                    of UNOSQ '26!
                  </p>
                </div>

                {/* Live Countdown Section - Days : Hours : Mins */}
                <div className="p1-countdown-section q-countdown-box-theme">
                  <div className="p1-countdown-header">
                    <FaCalendarAlt className="p1-cal-icon" />
                    <span>
                      Phase 2 Exam:{" "}
                      <strong>Sunday, 6th September 2026 at 10:00 AM</strong>
                    </span>
                  </div>

                  {!timeLeft.isPast ? (
                    <div className="p1-countdown-grid">
                      <div className="p1-countdown-box q-cd-box">
                        <span className="p1-cd-value">
                          {String(timeLeft.days).padStart(2, "0")}
                        </span>
                        <span className="p1-cd-label">Days</span>
                      </div>
                      <span className="p1-cd-colon">:</span>
                      <div className="p1-countdown-box q-cd-box">
                        <span className="p1-cd-value">
                          {String(timeLeft.hours).padStart(2, "0")}
                        </span>
                        <span className="p1-cd-label">Hours</span>
                      </div>
                      <span className="p1-cd-colon">:</span>
                      <div className="p1-countdown-box q-cd-box">
                        <span className="p1-cd-value">
                          {String(timeLeft.minutes).padStart(2, "0")}
                        </span>
                        <span className="p1-cd-label">Mins</span>
                      </div>
                    </div>
                  ) : (
                    <div className="p1-live-now-badge">
                      <span className="p1-live-dot"></span>
                      <span>🎉 Phase 2 is Live Today!</span>
                    </div>
                  )}
                </div>

                {/* Core Message Box */}
                <motion.div
                  className="p1-message-banner-box q-message-banner"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                >
                  <div className="p1-msg-content-wrapper">
                    <div
                      className="p1-msg-quote-icon q-quote-icon"
                      aria-hidden="true"
                    >
                      <FaQuoteLeft />
                    </div>
                    <div className="p1-msg-paragraphs">
                      <p className="p1-msg-p1">
                        Your stellar performance in <strong>Phase 1</strong> has
                        earned you a coveted place in the next round of
                        competition.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Practice Zone Recommendation Banner */}
                <motion.div
                  className="q-practice-banner-box"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22 }}
                >
                  <div className="q-practice-content">
                    <div className="q-practice-icon-wrap">
                      <FaRocket />
                    </div>
                    <div className="q-practice-text-wrap">
                      <h4 className="q-practice-title">
                        Practice from our Practice Zone! 🎯
                      </h4>
                      <p className="q-practice-desc">
                        Till the exam keep your preparation in peak form. Take unlimited topic tests and full-length mocks in our <strong>Practice Zone</strong>!
                      </p>
                    </div>
                  </div>
                  <motion.button
                    className="q-btn-practice-cta"
                    onClick={handleGoToPractice}
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>Go to Practice Zone</span>
                    <FaArrowRight />
                  </motion.button>
                </motion.div>

                {/* Action Controls & Calendar Reminders */}
                <div className="p1-action-footer">
                  <div className="p1-calendar-actions">
                    <motion.button
                      className="p1-btn-cal q-btn-gcal"
                      onClick={handleAddGoogleCalendar}
                      whileHover={{ scale: 1.03, y: -1 }}
                      whileTap={{ scale: 0.97 }}
                      title="Add Phase 2 reminder to Google Calendar"
                    >
                      <FaCalendarPlus />
                      <span>Add Phase 2 to Google Calendar</span>
                    </motion.button>
                  </div>

                  <div className="p1-dismiss-row">
                    <label className="p1-checkbox-label">
                      <input
                        type="checkbox"
                        checked={dontShowToday}
                        onChange={(e) => setDontShowToday(e.target.checked)}
                      />
                      <span>Don't auto-popup again today</span>
                    </label>

                    <div className="q-action-buttons">
                      <motion.button
                        className="p1-btn-primary q-btn-proceed"
                        onClick={handleViewPool}
                        whileHover={{ scale: 1.03, y: -1 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <FaCheckCircle />
                        <span>Go to My Phase 2 Pool</span>
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
